enum ScheduleTabSubDeepLinks {
  Appointment = "appointment",
  BookAppointment = "book-appointment",
  EditAppointment = "edit-appointment",
  GroupClassEvent = "group-class-event",
}

// In-app-only sub-target, set when navigating to the schedule-event-details
// modal so it can auto-open a nested action (e.g. the edit bottom sheet).
// Not part of any deep-link URL — the URL only carries `target` + the event id.
enum ScheduleEventDetailsSubDeepLinks {
  Edit = "edit",
}

enum RecurrenceValue {
  Biweekly = "Biweekly",
  DoesNotRepeat = "Does not repeat",
  Monthly = "Monthly",
  Weekly = "Weekly",
}

enum EventCancellationText {
  CancelAllFutureAppointmentsAlertMessage = "Once cancelled, you will need to reschedule with your provider, and availability may be limited. You can also cancel just this event and keep the rest in the series.",
  CancelAllFutureAppointmentsAlertTitle = "Are you sure you want to cancel this recurring event?",
  CancelAppointmentAlertMessage = "This event will be removed from your calendar and you will need to reschedule with your provider.",
  CancelAppointmentAlertMessageRecurring = "This event will be removed from your calendar, but future events in the series will remain unchanged.",
  CancelAppointmentAlertTitle = "Are you sure you want to cancel?",
  DeclineGroupClassEventAlertMessage = "This event will be removed from your calendar and you won't be able to join.",
  DeclineGroupClassEventAlertTitle = "Are you sure you want to decline?",
  Reason = "Equip patient or support needs to cancel",
}

enum EventCancellationError {
  CancelingAppointmentError = "Error canceling appointment",
  DecliningGroupClassEventError = "Error declining group class event",
}

// Datadog error messages for the v2 group registration flow
enum GroupRegistrationError {
  GroupDetailsLoadError = "Error loading v2 group details",
  RegisterError = "Error registering for group section",
  UnregisterError = "Error unregistering from group section",
}

enum CTAText {
  Back = "Back",
  Cancel = "Cancel",
  CancelAllFutureEvents = "Cancel all future events",
  CancelAppointment = "Cancel appointment",
  Confirm = "Confirm",
  Continue = "Continue",
  Decline = "Decline",
  DeclineEvent = "Decline this event",
  JoinZoom = "Join Zoom",
  RescheduleAppointment = "Reschedule appointment",
  SaveChanges = "Save changes",
}

enum ListEmptyCTAText {
  BookAppointment = "Book an appointment",
  InTreatmentCTA = "Did you know you can book appointments with your provider team using the Equip App? You can even set up recurring sessions.",
  LearnMore = "Learn more",
  NoAppointments = "No appointments scheduled",
  NoEvents = "No upcoming events",
  NotInTreatmentCTA = "Once you've finished your admissions tasks and start treatment, you'll be able to book and see upcoming appointments here.",
}

enum LearnMoreResource {
  title = "Learn more about our treatment",
  url = "https://equip.health/our-treatment/",
}

enum AppointmentCancellationReason {
  PatientReschedule = "PATIENT_RESCHEDULE",
  SupportReschedule = "SUPPORT_RESCHEDULE",
  User = "MYEQUIP",
}

// group_section_frequency codes (schedule-service reference table)
enum GroupSectionFrequencyCode {
  DoesNotRepeat = "DOES_NOT_REPEAT",
  Every4Weeks = "EVERY_4_WEEKS",
  EveryDay = "EVERY_DAY",
  EveryOtherWeek = "EVERY_OTHER_WEEK",
  EveryWeekday = "EVERY_WEEKDAY",
  FirstOfEveryMonth = "FIRST_OF_EVERY_MONTH",
  Weekly = "WEEKLY",
}

// user-facing copy for the v2 group registration flow (details screen, session list,
// confirm/edit bottom sheets, unregister alert)
enum GroupRegistrationText {
  AvailableSessionsDescription = "Please choose the option that works best for you:",
  AvailableSessionsTitle = "Available days and times",
  ConfirmRegistrationTitle = "Confirm registration",
  DeclineEvent = "Decline event",
  EditRegistration = "Edit registration",
  EditRegistrationTitle = "Edit or cancel registration",
  GroupDetailsLoadError = "Couldn't load group details. Please try again.",
  HostedByLabel = "Hosted by",
  RegisterCta = "Register for group",
  // "Registered" doubles as the success toast text and the registered-state tag label
  Registered = "Registered",
  RegistrationErrorMessage = "Registration failed. Please try again.",
  RegistrationGenericError = "Something went wrong. Please try again.",
  UnregisterConfirmMessage = "Once you unregister from this group, you may not be able to register again.",
  UnregisterConfirmTitle = "Are you sure you want to unregister?",
  UnregisterCta = "Unregister from this group",
  Unregistered = "Unregistered",
}

// group `groupTypeCode` values (mirrors schedule-service GroupType enum)
enum GroupTypeCode {
  CopingSkills = "COPING_SKILLS",
  Education = "EDUCATION",
  Orientation = "ORIENTATION",
  Support = "SUPPORT",
  Workshop = "WORKSHOP",
}

const GroupClassCarouselVariantValues: Record<
  GroupClassCarouselVariant,
  GroupClassCarouselVariant
> = {
  basicGroups: "basicGroups",
  promotedGroups: "promotedGroups",
  yourGroups: "yourGroups",
};

export {
  AppointmentCancellationReason,
  CTAText,
  EventCancellationError,
  EventCancellationText,
  GroupClassCarouselVariantValues,
  GroupRegistrationError,
  GroupRegistrationText,
  GroupSectionFrequencyCode,
  GroupTypeCode,
  LearnMoreResource,
  ListEmptyCTAText,
  RecurrenceValue,
  ScheduleEventDetailsSubDeepLinks,
  ScheduleTabSubDeepLinks,
};
